import RumClient from './RumClient';

/**
 * Telemetry helpers for the publisher review (bar raiser) stage.
 *
 * Every public function records a single CloudWatch RUM custom event under the
 * {@link PUBLISHER_REVIEW_EVENT_ID} event type, discriminated by a `subType`
 * field, then forwarded to {@link RumClient.recordEvent}.
 *
 * @module PublisherReviewTelemetry
 */

/** CloudWatch RUM event type shared by all publisher-review telemetry events. */
const PUBLISHER_REVIEW_EVENT_ID = 'publisher_review';

/** Recorded when a bar raiser clicks through to view a flagged duplicate match. */
interface DuplicationResultClickthroughEvent {
  /** Discriminator for the event union. */
  readonly subType: 'DuplicationResultClickthrough';
  /** Server-assigned content identifier under review. */
  readonly contentId: string;
  /** Bar raiser scan id — join key to the duplication scan results. */
  readonly scanId: string;
  /** The id of the matched content the reviewer clicked. */
  readonly matchedContentId: string;
}

/** Discriminated union of every event this module can emit. */
type PublisherReviewEvent = DuplicationResultClickthroughEvent;

/** Enriches the payload with a timestamp, then forwards it to CloudWatch RUM. */
function emit(payload: PublisherReviewEvent): void {
  RumClient.recordEvent(PUBLISHER_REVIEW_EVENT_ID, {
    ...payload,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Records a bar raiser clicking through to view a flagged duplicate match.
 *
 * @param params.contentId        - Content identifier under review.
 * @param params.scanId           - Bar raiser scan id correlating the results.
 * @param params.matchedContentId - Id of the matched content the reviewer clicked.
 */
export function recordPublisherReviewDuplicationClickthrough(
  params: Omit<DuplicationResultClickthroughEvent, 'subType'>,
): void {
  emit({ subType: 'DuplicationResultClickthrough', ...params });
}
